/**
 * Stepper — journey-style step indicator with free navigation.
 *
 * Renders a horizontal sequence of steps separated by chevrons. Each
 * step shows:
 *   - its number (or a ✓ when complete)
 *   - its label
 *   - active / complete / pending visual state
 *
 * Steps are clickable; navigation is free (no gating). Mark steps as
 * complete via the `complete` array on each step entry — the parent
 * decides what "complete" means for that step.
 *
 * Not a wizard primitive — this is the indicator + navigation control
 * only. The parent renders the actual step content keyed by `value`.
 */

import { AlertTriangle, Check, ChevronRight, Play } from "lucide-react";
import { cn } from "../primitives/utils";

export type StepperState = "complete" | "in_progress" | "pending";
export type StepperSeverity = "ok" | "amber" | "red";

export interface StepperStep {
  /** Stable identifier — used as the key + the `value` passed to onChange. */
  id: string;
  /** Label rendered next to the step number. */
  label: string;
  /** Whether this step has all required input. Drives the ✓ + colour.
   *  Backward-compat alias for `state === "complete"`. */
  complete?: boolean;
  /** Explicit step state — when provided, takes precedence over `complete`. */
  state?: StepperState;
  /** Severity overlay for this step — amber/red swap the state icon for
   *  AlertTriangle so the step reads as "attention required" regardless
   *  of whether it's pending, running, or complete. */
  severity?: StepperSeverity;
}

export interface StepperProps {
  steps: StepperStep[];
  /** Currently active step id. */
  value: string;
  /** Called when the user clicks any step. */
  onChange: (id: string) => void;
  /** Accessible name for the step list. */
  label?: string;
  className?: string;
}

// Resolve the step's effective state from either explicit `state` or the
// legacy `complete` boolean. Estimating still uses `complete` so we keep
// the alias working — only callers wanting the in_progress/pending split
// (work-unit detail) pass `state` explicitly.
function resolveState(step: StepperStep): StepperState {
  if (step.state) return step.state;
  if (step.complete) return "complete";
  return "pending";
}

interface StepIconResult {
  Component: typeof Check | typeof Play | typeof AlertTriangle | null;
  colour: string;
}

// Universal severity lexicon — keep this in sync with the equivalent
// helper on WorkUnitDetailPage so the stepper and the per-row icons read
// identically.
function resolveStepIcon(
  state: StepperState,
  severity: StepperSeverity | undefined,
  isActive: boolean,
): StepIconResult {
  // Triangle wins for amber/red, regardless of state — same rule as the
  // row-level icons on WorkUnitDetailPage. When the step is active (pink
  // pill background) the icon flips to primary-foreground so it stays
  // readable; only the non-active state uses the literal red/amber hue.
  if (severity === "red") {
    return {
      Component: AlertTriangle,
      colour: isActive ? "text-primary-foreground" : "text-red-600",
    };
  }
  if (severity === "amber") {
    return {
      Component: AlertTriangle,
      colour: isActive ? "text-primary-foreground" : "text-amber-600",
    };
  }
  if (state === "complete") {
    return {
      Component: Check,
      colour: isActive ? "text-primary-foreground" : "text-emerald-500",
    };
  }
  if (state === "in_progress") {
    return {
      Component: Play,
      // emerald (same family as the complete Check) — pink primary read
      // as red against the active-step pink pill background. Active steps
      // still get primary-foreground (white) so the icon reads on pink.
      colour: isActive ? "text-primary-foreground" : "text-emerald-600",
    };
  }
  return { Component: null, colour: "" };
}

export function Stepper({
  steps,
  value,
  onChange,
  label = "Estimate steps",
  className,
}: StepperProps) {
  return (
    <nav
      aria-label={label}
      className={cn(
        // Wrap onto multiple rows when the row can't fit the full step
        // sequence (narrow viewports or many process steps). A horizontal
        // scrollbar here was awkward on touch and looked broken on
        // desktop when it appeared intermittently.
        "flex flex-wrap items-center gap-y-1",
        className,
      )}
    >
      {steps.map((step, idx) => {
        const isActive = step.id === value;
        const state = resolveState(step);
        const { Component: Icon, colour: iconColour } = resolveStepIcon(
          state,
          step.severity,
          isActive,
        );
        // Pending steps without severity stay greyed; complete/in_progress
        // and any severity-flagged step keep the active text colour.
        const isMuted =
          state === "pending" && (!step.severity || step.severity === "ok");
        return (
          <div key={step.id} className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => onChange(step.id)}
              aria-current={isActive ? "step" : undefined}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-primary",
                isActive
                  ? "bg-primary text-primary-foreground font-medium"
                  : isMuted
                    ? "text-muted-foreground hover:bg-muted"
                    : "text-foreground hover:bg-muted",
              )}
            >
              {Icon && (
                <Icon
                  className={cn("size-3.5 shrink-0", iconColour)}
                  aria-hidden
                />
              )}
              <span>{step.label}</span>
            </button>
            {idx < steps.length - 1 && (
              <ChevronRight
                className="size-4 text-muted-foreground shrink-0"
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </nav>
  );
}
